const API_URL = process.env.REACT_APP_API_URL;

/**
 * Wrapper fetch centralisé :
 * - ajoute automatiquement le token JWT si présent
 * - tente un refresh silencieux en cas de 401, puis rejoue la requête une fois
 * - lève une erreur exploitable (avec le détail renvoyé par l'API) en cas d'échec
 */
async function request(path, { method = 'GET', body, auth = true, isRetry = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };

  if (auth) {
    const token = localStorage.getItem('access_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && auth && !isRetry) {
    const refreshed = await tryRefreshToken();
    if (refreshed === true) {
      return request(path, { method, body, auth, isRetry: true });
    }
    const error = new Error('Session expirée');
    if (refreshed === false) {
      // Le serveur a explicitement rejeté le refresh token : la session est réellement expirée.
      error.status = 401;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/admin/connexion';
    }
    // refreshed === null : échec réseau/abandon de requête (ex. rechargement de page), pas une
    // vraie expiration de session. On n'efface pas les tokens ni ne redirige, et on ne marque
    // pas l'erreur comme 401 pour que les appelants ne la traitent pas comme une déconnexion.
    return Promise.reject(error);
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(data?.error || 'Erreur serveur');
    error.status = response.status;
    error.errors = data?.errors || {};
    throw error;
  }

  return data;
}

/**
 * Retourne true si le refresh a réussi, false si le serveur l'a explicitement rejeté
 * (refresh token invalide/expiré), ou null si l'échec est indéterminé (erreur réseau,
 * requête abandonnée par un rechargement de page...) — ce dernier cas ne doit pas être
 * traité comme une expiration de session.
 */
async function tryRefreshToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return false;
  }

  let response;
  try {
    response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    return null;
  }

  if (!response.ok) {
    return false;
  }

  try {
    const data = await response.json();
    localStorage.setItem('access_token', data.access_token);
    return true;
  } catch {
    return null;
  }
}

const client = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

export default client;
